import googleChrome from '../assets/brands/google-chrome.svg';
import microsoft from '../assets/brands/microsoft.svg';
import microsoftEdge from '../assets/brands/microsoft-edge.svg';

// FD-612 — third-party brand marks, used nominatively: to name the sign-in provider
// (Microsoft) and the browser the helper is for (Edge, Chrome).
//
// A DELIBERATE EXCEPTION TO THE LUCIDE-ONLY ICON RULE. These are not icons, they are
// other companies' marks, and a monochrome redraw of a trademark is a worse
// misrepresentation than the real thing. Sources and licences:
// `src/assets/brands/README.md`. Their colours are THEIRS and never enter the
// theme tokens.
//
// Rendered as `<img>`, not inlined: each SVG carries its own gradient ids (`a`, `b`…),
// and an image keeps those ids in its own document, so two marks on one page cannot
// collide (the trap AirBridgeMark solves with `useId`).

export type Brand = 'microsoft' | 'chrome' | 'edge';

const SOURCES: Record<Brand, { src: string; name: string }> = {
  microsoft: { src: microsoft, name: 'Microsoft' },
  chrome: { src: googleChrome, name: 'Google Chrome' },
  edge: { src: microsoftEdge, name: 'Microsoft Edge' },
};

export interface BrandLogoProps {
  brand: Brand;
  /** Rendered height and width in px. */
  size?: number;
  /**
   * When true the mark is announced by its brand name. Default false: the mark
   * almost always sits beside text that already names the brand, and a screen
   * reader saying it twice is noise.
   */
  labelled?: boolean;
}

export default function BrandLogo({ brand, size = 20, labelled = false }: BrandLogoProps) {
  const { src, name } = SOURCES[brand];
  return (
    <img
      src={src}
      width={size}
      height={size}
      alt={labelled ? name : ''}
      aria-hidden={labelled ? undefined : true}
      draggable={false}
      data-brand={brand}
      style={{ display: 'block', flexShrink: 0 }}
    />
  );
}
